import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminAgentHealth, AdminCluster, AdminFleet } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { Meter } from "@/ui/Meter";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { ReasonDialog } from "@/features/admin/ReasonDialog";
import { absoluteTime, relativeTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/fleet")({
  component: CapacityScreen,
});

/**
 * What is actually left in each region.
 *
 * The agent has been reporting what a cluster can allocate and what is
 * committed on every heartbeat since it existed, and nothing read it. An
 * operator deciding whether to let the next twenty accounts in was doing it by
 * feel.
 *
 * Committed, not used. A service that has reserved four cores and is idle has
 * still taken them out of what the scheduler can hand to anybody else, and it
 * is that number — not the graph of what is busy — that decides whether the
 * next deployment finds a home.
 */
function CapacityScreen() {
  // One state for the cluster whose status is being changed, and to what.
  const [changing, setChanging] = useState<{ agent: AdminAgentHealth; to: ClusterStatus } | null>(
    null,
  );

  const capacity = useQuery({
    queryKey: ["platform-fleet"],
    queryFn: ({ signal }) => api.get<AdminFleet>("/admin/fleet", undefined, signal),
    refetchInterval: 30000,
  });

  const clusters = capacity.data?.clusters ?? [];
  const agents = capacity.data?.agents ?? [];
  const events = capacity.data?.events ?? [];
  // An agent that has stopped checking in leaves every service on that cluster
  // describing a desired state nothing is acting on.
  const silent = agents.filter((a) => a.status !== "disabled" && agentStale(a));

  return (
    <div className="flex flex-col gap-4">
      {silent.length > 0 && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {silent.length === 1
              ? `The agent on ${silent[0]?.slug} has stopped checking in`
              : `${silent.length} agents have stopped checking in`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            Services on those clusters are describing a desired state nothing is acting on.
          </p>
        </div>
      )}

      {/* With nothing enrolled, there is one thing to say and three places
          this used to say it: "No clusters", then "No agents", then "Nothing
          reported" — three framed sections, stacked, about things that cannot
          exist until a cluster does. An installation with no fleet gets one
          answer and the sentence that tells an operator what to do about it.

          # WHY THE FOOTNOTE MOVED UP

          "These are reservations, not usage" sat between the clusters and the
          agents table with no heading over it, orphaned between two things it
          belonged to neither of. It belongs to the capacity figures, so it
          goes under them. */}
      {capacity.isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : capacity.isError ? (
        <ErrorState error={capacity.error} onRetry={() => void capacity.refetch()} />
      ) : clusters.length === 0 ? (
        <Panel padded={false}>
          <EmptyState
            title="No clusters are enrolled"
            description="Nothing can be scheduled until one is. A cluster joins by running the agent with an enrolment token, and everything on this screen — capacity, agents, what they report — appears once it has."
          />
        </Panel>
      ) : (
        <>
          {clusters.map((c) => <ClusterCard key={c.id} cluster={c} />)}
          <p className="t-meta max-w-[78ch]">
            These are reservations, not usage. A service holding four cores and
            doing nothing has still taken them out of what the scheduler can
            offer anybody else, and that is the number that decides whether the
            next deployment finds a home.
          </p>
        </>
      )}

      {clusters.length > 0 && (
      <Panel
        title="Agents"
        description="Whether each cluster is still listening, and whether it has applied what it was last told."
        padded={false}
      >
        {agents.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No agents" description="No cluster has enrolled." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[18%]">Cluster</Th>
                  <Th width="w-[13%]">Status</Th>
                  <Th width="w-[14%]" align="right">Last seen</Th>
                  <Th width="w-[14%]" align="right">Applied</Th>
                  <Th width="w-[13%]">Agent</Th>
                  <Th width="w-[9%]" numeric>Services</Th>
                  <Th width="w-[19%]" align="right">Scheduling</Th>
                </Tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <Tr
                    key={a.cluster_id}
                    className={agentStale(a) && a.status !== "disabled"
                      ? "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]"
                      : undefined}
                  >
                    <Td clip title={`${a.region} · ${a.kubernetes_version ?? ""}`}
                        secondary={a.region}>
                      {a.slug}
                    </Td>
                    <Td>
                      <Chip tone={statusTone(a.status)}>{a.status}</Chip>
                    </Td>
                    <Td align="right"
                        title={a.agent_last_seen_at ? absoluteTime(a.agent_last_seen_at) : undefined}>
                      {a.agent_last_seen_at ? shortTime(a.agent_last_seen_at) : "never"}
                    </Td>
                    {/* When the agent last applied a revision, which is a
                        different fact from when it last said hello. An agent
                        can heartbeat happily and apply nothing. */}
                    <Td align="right"
                        title={a.applied_revision
                          ? `revision ${a.applied_revision}`
                          : "This agent has not applied anything."}>
                      {a.applied_at ? shortTime(a.applied_at) : "—"}
                    </Td>
                    <Td clip title={a.agent_version || undefined} className="mono text-[13px]">
                      {a.agent_version || "—"}
                    </Td>
                    <Td numeric>{a.services}</Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-1">
                        {a.status !== "active" && (
                          <Button tone="secondary" size="dense"
                                  onClick={() => setChanging({ agent: a, to: "active" })}>
                            Take work
                          </Button>
                        )}
                        {a.status === "active" && (
                          <Button tone="secondary" size="dense"
                                  onClick={() => setChanging({ agent: a, to: "draining" })}>
                            Drain
                          </Button>
                        )}
                        {a.status !== "disabled" && (
                          <Button tone="secondary" size="dense"
                                  className="text-[var(--danger)]"
                                  onClick={() => setChanging({ agent: a, to: "disabled" })}>
                            Disable
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
      )}

      {clusters.length > 0 && (
      <Panel
        title="What the clusters are reporting"
        description="A pod that will not start, an image that cannot be pulled, a volume that cannot be bound. The agent has been writing these since it existed."
        padded={false}
      >
        {events.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="Nothing reported" description="No cluster has anything to say." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[14%]">Cluster</Th>
                  <Th width="w-[17%]">Tenant</Th>
                  <Th width="w-[14%]">Subject</Th>
                  <Th width="w-[13%]">State</Th>
                  <Th width="w-[29%]">Reason</Th>
                  <Th width="w-[13%]" align="right">When</Th>
                </Tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <Tr key={e.id}>
                    <Td clip>{e.cluster_slug || "—"}</Td>
                    <Td clip title={e.organisation_name || undefined}>
                      {e.organisation_id ? (
                        <Link to="/admin/$orgId" params={{ orgId: e.organisation_id }}
                              className="hover:text-[var(--primary)]">
                          {e.organisation_name || e.organisation_id}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td clip title={e.subject_id}>{e.subject_type}</Td>
                    <Td clip title={e.observed_state}>{e.observed_state}</Td>
                    {/* The advice as well as the reason. The agent writes both
                        and the second is the half a support person can act on. */}
                    <Td clip title={e.technical_detail || e.advice || e.reason || undefined}>
                      <span className="text-[var(--text-secondary)]">
                        {e.reason || e.advice || "—"}
                      </span>
                    </Td>
                    <Td align="right" title={absoluteTime(e.observed_at)}>
                      {shortTime(e.observed_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
      )}

      {changing && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setChanging(null)}
          title={`${CHANGES[changing.to].title}: ${changing.agent.slug}`}
          description={CHANGES[changing.to].description}
          confirmLabel={CHANGES[changing.to].confirm}
          danger={changing.to === "disabled"}
          action={(reason) =>
            api.put<void>(`/admin/clusters/${changing.agent.cluster_id}/status`, {
              status: changing.to,
              reason,
            })
          }
          onDone={async () => {
            setChanging(null);
            await capacity.refetch();
          }}
        />
      )}
    </div>
  );
}

type ClusterStatus = "active" | "draining" | "disabled";

const CHANGES: Record<ClusterStatus, { title: string; description: string; confirm: string }> = {
  active: {
    title: "Let this cluster take work",
    description: "New services can be placed here again.",
    confirm: "Take work",
  },
  draining: {
    title: "Drain this cluster",
    description:
      "What is running keeps running; nothing new is placed here. The move to make before an upgrade, well before anybody touches the machines.",
    confirm: "Drain it",
  },
  disabled: {
    title: "Disable this cluster",
    description: "Out of service. Nothing new is placed here and it is not counted as capacity.",
    confirm: "Disable it",
  },
};

/** Five minutes without a heartbeat. The agent checks in far more often. */
function agentStale(a: AdminAgentHealth): boolean {
  if (!a.agent_last_seen_at) return true;
  return Date.now() - new Date(a.agent_last_seen_at).getTime() > 5 * 60 * 1000;
}

function statusTone(status: string): ChipTone | undefined {
  switch (status) {
    case "active":
      return "up";
    case "draining":
      return "warn";
    case "unreachable":
      return "down";
    case "disabled":
      return undefined;
    default:
      return undefined;
  }
}

function ClusterCard({ cluster: c }: { cluster: AdminCluster }) {
  const cpuUsed = fraction(c.committed_cpu_millicores, c.allocatable_cpu_millicores);
  const memUsed = fraction(c.committed_memory_mib, c.allocatable_memory_mib);
  const stale = c.agent_last_seen_at
    ? Date.now() - +new Date(c.agent_last_seen_at) > 5 * 60 * 1000
    : true;

  return (
    <Panel
      title={c.slug}
      description={`${c.region || "Unknown region"} · ${c.node_count} nodes`}
      actions={
        <div className="flex items-center gap-2">
          {c.supports_isolation && <Chip>Isolated workloads</Chip>}
          <Chip tone={c.status === "ready" ? "live" : "warn"}>{c.status}</Chip>
          {stale && <Chip tone="down">Agent silent</Chip>}
        </div>
      }
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <CapacityBar
          label="Processor"
          used={cpuUsed}
          detail={`${format(c.committed_cpu_millicores / 1000)} of ${format(
            c.allocatable_cpu_millicores / 1000,
          )} cores committed`}
          remaining={`${format(
            (c.allocatable_cpu_millicores - c.committed_cpu_millicores) / 1000,
          )} cores free`}
        />
        <CapacityBar
          label="Memory"
          used={memUsed}
          detail={`${format(c.committed_memory_mib / 1024)} of ${format(
            c.allocatable_memory_mib / 1024,
          )} GB committed`}
          remaining={`${format(
            (c.allocatable_memory_mib - c.committed_memory_mib) / 1024,
          )} GB free`}
        />
      </div>

      <div className="mt-5 grid gap-4 border-t border-[var(--border)] pt-4 sm:grid-cols-4">
        <Fact label="Tenants" value={String(c.organisations)} />
        <Fact label="Services" value={String(c.services)} />
        <Fact label="Kubernetes" value={c.kubernetes_version || "—"} />
        <Fact
          label="Agent seen"
          value={c.agent_last_seen_at ? relativeTime(c.agent_last_seen_at) : "never"}
          title={c.agent_last_seen_at ? absoluteTime(c.agent_last_seen_at) : undefined}
        />
      </div>
    </Panel>
  );
}

function CapacityBar({
  label,
  used,
  detail,
  remaining,
}: {
  label: string;
  used: number;
  detail: string;
  remaining: string;
}) {
  // The threshold colours used to be computed here, with a third set of
  // numbers: red at 90%, amber at 75%. The plan card used 80% and the account
  // overview 75%, so "this is getting full" meant three different things on
  // three screens of one product. Meter owns the rule now.
  //
  // `remaining` keeps its own colour below, because it is a sentence about
  // what is left rather than a reading of the bar, and on this screen it is
  // the line an operator acts on.
  const tone =
    used >= 0.9
      ? "var(--danger)"
      : used >= 0.75
        ? "var(--warning)"
        : "var(--success)";

  return (
    <div>
      <Meter
        label={label}
        value={Math.min(used, 1)}
        limit={1}
        display={`${Math.round(used * 100)}%`}
        size="sm"
      />
      <p className="mt-1.5 text-[12px] text-[var(--text-muted)]">{detail}</p>
      <p className="text-[12px] font-medium" style={{ color: tone }}>
        {remaining}
      </p>
    </div>
  );
}

function Fact({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div>
      <p className="text-[12px] text-[var(--text-muted)]">{label}</p>
      <p className="mt-0.5 text-[13px] font-medium text-[var(--text-primary)]" title={title}>
        {value}
      </p>
    </div>
  );
}

function fraction(part: number, whole: number): number {
  return whole > 0 ? part / whole : 0;
}

function format(n: number): string {
  return n >= 10 ? String(Math.round(n)) : n.toFixed(1).replace(/\.0$/, "");
}
